<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Configure data stream</h1>

                    <a :href="`/#/admin/integrations/pipelines/details/${tid}/${id}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        Back
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div v-if="currentDataStream" class="alert alert-info" role="alert">
                <strong>Current data stream:</strong> {{ currentDataStream }}
            </div>

            <h5>Available data streams</h5>
            <br />

            <div v-if="dataStreamsError" class="alert alert-warning" role="alert">
                <strong>Note:</strong> {{ dataStreamsError }}
                <br />
                <span v-if="supportsDataStreams">You can manually enter a data stream name below.</span>
            </div>

            <div v-if="dataStreams.length > 0">
                <div v-for="(ds, i) in dataStreams" class="form-group form-check">
                    <input @click="handleChangeDataStreamCommandV1(ds)" type="radio" class="form-check-input" :id="`datastream${i}`" name="dataStream" :value="ds" :checked="dataStream === ds">
                    <label class="form-check-label" :for="`datastream${i}`">{{ds}}</label>
                </div>
            </div>

            <div v-if="supportsDataStreams" class="mt-4">
                <h6>{{ dataStreams.length > 0 ? 'Or enter' : 'Enter' }} a custom data stream name:</h6>
                <div class="form-group">
                    <label for="customDataStream">Custom data stream</label>
                    <input 
                        v-model="customDataStreamInput" 
                        type="text" 
                        class="form-control" 
                        id="customDataStream" 
                        placeholder="e.g., Sheet1, MyData, etc."
                        @keyup.enter="handleCustomDataStreamSubmit"
                    />
                    <small class="form-text text-muted">
                        Enter a custom data stream name (e.g., Excel sheet name) and click "Set Custom Stream"
                    </small>
                </div>
                <button 
                    @click="handleCustomDataStreamSubmit" 
                    class="btn btn-primary"
                    :disabled="!customDataStreamInput || customDataStreamInput.trim() === ''">
                    Set Custom Stream
                </button>
                <button 
                    v-if="customDataStreamInput !== currentDataStream && currentDataStream"
                    @click="customDataStreamInput = currentDataStream" 
                    class="btn btn-secondary ml-2">
                    Reset to Current
                </button>
            </div>

            <div v-if="!supportsDataStreams && dataStreams.length === 0" class="alert alert-info" role="alert">
                This connector does not support multiple data streams.
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
    import { useId, useOrganizationUnitId } from '@common/composables'
    import { useAdminConfigurePipelineDataStreamPage, useAdminConfigurePipelinePropertiesPage } from '@Integrations/Pipelines/pages-core'
    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'

    document.title = "Configure data streams"

    const tid = useOrganizationUnitId()
    const id = useId()

    const connectorId = ref()
    const customDataStreamInput = ref('')

    const {
        useCurrentUserQuery,
        usePipelineDetailsQuery,
        useConnectorDataStreamsQuery,
        useChangeDataStreamCommand,
    } = useAdminConfigurePipelineDataStreamPage()

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { runPipelineDetailsQueryV1, modelPipelineDetailsQueryV1 } = usePipelineDetailsQuery(id)
    const dataStream = computed<string>(() => modelPipelineDetailsQueryV1.value?.DataStream)
    const currentDataStream = computed<string | null>(() => dataStream.value || null)

    const { runConnectorDataStreamsQueryV1, modelConnectorDataStreamsQueryV1 } = useConnectorDataStreamsQuery(connectorId)
    const dataStreams = computed<string[]>(() => modelConnectorDataStreamsQueryV1.value?.DataStreams || [])
    const supportsDataStreams = computed<boolean>(() => modelConnectorDataStreamsQueryV1.value?.SupportsDataStreams || false)
    const dataStreamsError = computed<string | undefined>(() => modelConnectorDataStreamsQueryV1.value?.DataStreamsError)

    const { invokeChangeDataStreamCommandV1, succeedChangeDataStreamCommandV1 } = useChangeDataStreamCommand(id, tid, userId)

    onMounted(() => {
        runCurrentUserQueryV1()
        runPipelineDetailsQueryV1()
    })

    watch(connectorId, () => {
        runConnectorDataStreamsQueryV1()
    })

    watch(modelPipelineDetailsQueryV1, () => {
        connectorId.value = modelPipelineDetailsQueryV1.value.ConnectorId
    })

    watch(succeedChangeDataStreamCommandV1, () => {
        if (succeedChangeDataStreamCommandV1.value) {
            runPipelineDetailsQueryV1()
            // Clear the input after successful submission
            customDataStreamInput.value = ''
        }
    })

    // Initialize the input with the current data stream value when it's loaded
    watch(currentDataStream, (newValue) => {
        if (newValue && !customDataStreamInput.value) {
            customDataStreamInput.value = newValue
        }
    }, { immediate: true })

    function handleChangeDataStreamCommandV1(dataStream: string){
        invokeChangeDataStreamCommandV1({ DataStream: dataStream })
    }

    function handleCustomDataStreamSubmit() {
        const trimmedStream = customDataStreamInput.value.trim()
        if (trimmedStream) {
            invokeChangeDataStreamCommandV1({ DataStream: trimmedStream })
        }
    }
</script>